// src/pages/TasksPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import DataGrid from '../components/DataGrid'; // [marker: step1-templates-grids v1]

const PRIORITY_COLORS = {
  urgent: { bg: '#fef2f2', color: '#dc2626', label: 'Urgent' },
  high:   { bg: '#fffbeb', color: '#d97706', label: 'High' },
  normal: { bg: '#f0f4f8', color: '#1a3a5c', label: 'Normal' },
  low:    { bg: '#f9fafb', color: '#9ca3af', label: 'Low' },
};

const STATUS_COLORS = {
  open:        'badge-sent',
  in_progress: 'badge-grace',
  pending:     'badge-draft',
  complete:    'badge-active',
  cancelled:   'badge-lapsed',
};

const SNOOZE_PRESETS = [
  { key: '1h',         label: '1 hour' },
  { key: '4h',         label: '4 hours' },
  { key: 'today_eod',  label: 'End of today' },
  { key: 'tomorrow',   label: 'Tomorrow 9am' },
  { key: 'next_week',  label: 'Next week' },
];

export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('mine');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [snoozeMenuTaskId, setSnoozeMenuTaskId] = useState(null);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ view, limit: '5000' });
      if (statusFilter) params.append('status', statusFilter);
      const res = await api.get(`/tasks?${params}`);
      setTasks(res.data.data || []);
    } catch { toast.error('Failed to load tasks.'); }
    finally { setLoading(false); }
  }, [view, statusFilter]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  useEffect(() => {
    api.get('/tasks/unread-count').then(r => setUnreadCount(parseInt(r.data.unread_tasks || 0))).catch(() => {});
    api.get('/staff/users').then(r => setStaffList(r.data)).catch(() => {});
  }, []);

  // Close snooze menu when clicking outside
  useEffect(() => {
    if (!snoozeMenuTaskId) return;
    const onDocClick = () => setSnoozeMenuTaskId(null);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [snoozeMenuTaskId]);

  const handleComplete = async (taskId) => {
    try {
      await api.patch(`/tasks/${taskId}`, { status: 'complete' });
      toast.success('Task completed!');
      loadTasks();
      if (selectedTask?.id === taskId) setSelectedTask(null);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
  };

  const handleSnooze = async (taskId, duration) => {
    try {
      await api.post(`/tasks/${taskId}/snooze`, { duration });
      toast.success('Task snoozed.');
      loadTasks();
      setSnoozeMenuTaskId(null);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to snooze.'); }
  };

  const handleUnsnooze = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/unsnooze`);
      toast.success('Snooze cleared.');
      loadTasks();
    } catch (err) { toast.error('Failed.'); }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success('Task deleted.');
      loadTasks();
      if (selectedTask?.id === task.id) setSelectedTask(null);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete task.'); }
  };

  const overdueTasks = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'complete');

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Tasks</h1>
          <p className="page-subtitle">
            {unreadCount > 0 && <span style={{ color: 'var(--danger)', fontWeight: 700 }}>{unreadCount} unread · </span>}
            {tasks.length} tasks
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>+ New Task</button>
      </div>

      {/* Overdue Alert */}
      {overdueTasks.length > 0 && (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 'var(--radius)', padding: '10px 16px', marginBottom: 20, fontSize: '0.875rem', color: 'var(--danger)' }}>
          ⚠ {overdueTasks.length} overdue task{overdueTasks.length > 1 ? 's' : ''}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selectedTask ? '1fr 380px' : '1fr', gap: 20 }}>
        {/* Task List */}
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {['mine','assigned_to_me','created_by_me','snoozed','all'].map(v => (
              <button key={v} className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => { setView(v); setSelectedTask(null); }}>
                {v === 'mine' ? 'My Tasks'
                  : v === 'assigned_to_me' ? 'Assigned to Me'
                  : v === 'created_by_me' ? 'Created by Me'
                  : v === 'snoozed' ? '💤 Snoozed'
                  : 'All Tasks'}
              </button>
            ))}
            <select className="form-select" style={{ width: 'auto', marginLeft: 'auto' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="pending">Pending</option>
              <option value="complete">Complete</option>
            </select>
          </div>

          <DataGrid
            gridKey="tasks"
            rows={tasks}
            loading={loading}
            onRowClick={t => setSelectedTask(t)}
            height="calc(100vh - 290px)"
            emptyText="No tasks found — you're all caught up!"
            columns={[
              { key: 'title', label: 'Task', width: 280,
                render: t => {
                  const unread = t.assigned_to_staff_id === user?.id && !t.is_read_by_assignee && t.status !== 'complete';
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600,
                                   color: t.status === 'complete' ? 'var(--gray-400)' : 'var(--gray-900)',
                                   textDecoration: t.status === 'complete' ? 'line-through' : 'none' }}>
                      {unread && <span title="Unread" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--danger)', flexShrink: 0 }} />}
                      <span style={{ width: 4, height: 16, borderRadius: 2, background: PRIORITY_COLORS[t.priority]?.color || 'var(--gray-300)', flexShrink: 0 }} />
                      {t.title}
                      {t.is_automated && <span style={{ fontSize: '0.65rem', background: 'var(--navy-ghost)', color: 'var(--navy)', padding: '1px 5px', borderRadius: 3 }}>AUTO</span>}
                    </span>
                  );
                } },
              { key: 'status', label: 'Status', width: 105, text: t => (t.status || '').replace('_', ' '),
                render: t => <span className={`badge ${STATUS_COLORS[t.status] || 'badge-draft'}`}>{(t.status || '').replace('_', ' ')}</span> },
              { key: 'priority', label: 'Priority', width: 85, text: t => PRIORITY_COLORS[t.priority]?.label || t.priority,
                get: t => ({ urgent: 1, high: 2, normal: 3, low: 4 }[t.priority] || 5),
                render: t => <span style={{ fontSize: '0.75rem', background: PRIORITY_COLORS[t.priority]?.bg, color: PRIORITY_COLORS[t.priority]?.color, padding: '1px 8px', borderRadius: 12, fontWeight: 600 }}>{PRIORITY_COLORS[t.priority]?.label}</span> },
              { key: 'due_date', label: 'Due', width: 100, type: 'date',
                render: t => t.due_date ? <span style={{ color: new Date(t.due_date) < new Date() && t.status !== 'complete' ? 'var(--danger)' : undefined }}>{fmtDate(t.due_date)}</span> : null },
              { key: 'assigned_to', label: 'Assigned To', width: 130, get: t => [t.assigned_to_first_name, t.assigned_to_last_name].filter(Boolean).join(' ') },
              { key: 'created_by', label: 'Created By', width: 130, get: t => [t.created_by_first_name, t.created_by_last_name].filter(Boolean).join(' ') },
              { key: 'linked_entity_name', label: 'Linked To', width: 180,
                render: t => t.linked_entity_name ? <span onClick={e => e.stopPropagation()}><LinkedEntityChip type={t.linked_entity_type} id={t.linked_entity_id} name={t.linked_entity_name} /></span> : null },
              { key: 'snoozed_until', label: 'Snoozed Until', width: 140, defaultHidden: true,
                get: t => (t.snoozed_until && new Date(t.snoozed_until) > new Date() ? t.snoozed_until : null),
                text: t => (t.snoozed_until && new Date(t.snoozed_until) > new Date() ? fmtDateTime(t.snoozed_until) : '') },
              { key: 'note_count', label: 'Notes', width: 65, type: 'number' },
              { key: 'description', label: 'Description', width: 220, defaultHidden: true },
              { key: 'created_at', label: 'Created', width: 100, type: 'date', defaultHidden: true },
              { key: 'completed_at', label: 'Completed', width: 100, type: 'date', defaultHidden: true },
              { key: 'actions', label: 'Actions', width: 230, text: () => '',
                render: t => {
                  const isSnoozed = t.snoozed_until && new Date(t.snoozed_until) > new Date();
                  return (
                    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                      {t.status !== 'complete' && (
                        <button className="btn btn-secondary btn-sm" style={{ padding: '0 8px', height: 24 }} onClick={() => handleComplete(t.id)}>✓ Done</button>
                      )}
                      {t.status !== 'complete' && (isSnoozed ? (
                        <button className="btn btn-ghost btn-sm" style={{ padding: '0 8px', height: 24 }} onClick={() => handleUnsnooze(t.id)} title={`Snoozed until ${fmtDateTime(t.snoozed_until)}`}>💤 Unsnooze</button>
                      ) : (
                        <select className="form-select" style={{ height: 24, padding: '0 4px', fontSize: '0.78rem', width: 100 }} value=""
                                onChange={e => e.target.value && handleSnooze(t.id, e.target.value)}>
                          <option value="">💤 Snooze…</option>
                          {SNOOZE_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                        </select>
                      ))}
                      <button className="btn btn-ghost btn-sm" style={{ padding: '0 6px', height: 24, color: 'var(--danger)' }} onClick={() => handleDelete(t)} title="Delete task">✕</button>
                    </span>
                  );
                } },
            ]}
          />
        </div>

        {/* Task Detail Panel */}
        {selectedTask && (
          <TaskDetailPanel
            taskId={selectedTask.id}
            staffList={staffList}
            onClose={() => setSelectedTask(null)}
            onUpdated={() => { loadTasks(); setSelectedTask(null); }}
          />
        )}
      </div>

      {showCreateModal && (
        <CreateTaskModal
          staffList={staffList}
          onClose={() => setShowCreateModal(false)}
          onSaved={() => { setShowCreateModal(false); loadTasks(); }}
        />
      )}
    </div>
  );
}

// ─── LinkedEntityChip ────────────────────────────────────────────────────────
function LinkedEntityChip({ type, id, name }) {
  if (!name) return null;
  const url =
    type === 'contact'  ? `/contacts/${id}` :
    type === 'business' ? `/members/${id}`  :
    type === 'event'    ? `/events/${id}`   : null;
  const icon = type === 'contact' ? '👤' : type === 'business' ? '🏢' : type === 'event' ? '📅' : '🔗';
  const inner = (
    <span style={{
      fontSize: '0.72rem',
      background: '#eef2ff',
      color: '#3730a3',
      padding: '2px 8px',
      borderRadius: 10,
      fontWeight: 500,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
    }}>
      <span>{icon}</span>
      <span>{name}</span>
    </span>
  );
  if (!url) return inner;
  return (
    <Link to={url} onClick={e => e.stopPropagation()} style={{ textDecoration: 'none' }}>
      {inner}
    </Link>
  );
}

// ─── LinkedEntityPicker ──────────────────────────────────────────────────────
// Lets you pick a contact OR a business (member). Used in Create/Edit modals.
function LinkedEntityPicker({ value, onChange }) {
  // value: { type: 'contact'|'business'|null, id: number|null, name: string|null }
  const [type, setType] = useState(value?.type || '');
  const [selected, setSelected] = useState(value?.id ? { id: value.id, name: value.name } : null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!type || !search || search.length < 2 || selected) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        if (type === 'contact') {
          const res = await api.get(`/contacts?search=${encodeURIComponent(search)}&limit=10`);
          setResults((res.data.data || []).map(c => ({
            id: c.id,
            name: `${c.first_name} ${c.last_name}`,
            subtitle: c.email || c.business_name || 'standalone',
          })));
        } else if (type === 'business') {
          const res = await api.get(`/businesses?search=${encodeURIComponent(search)}&limit=10`);
          const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
          setResults(list.map(b => ({
            id: b.id,
            name: b.business_name,
            subtitle: b.status || '',
          })));
        }
      } catch (_) { setResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, type, selected]);

  const pick = (item) => {
    setSelected(item);
    setSearch('');
    setResults([]);
    onChange({ type, id: item.id, name: item.name });
  };

  const clear = () => {
    setSelected(null);
    setSearch('');
    setResults([]);
    onChange({ type: null, id: null, name: null });
  };

  const changeType = (newType) => {
    setType(newType);
    setSelected(null);
    setSearch('');
    setResults([]);
    onChange({ type: null, id: null, name: null });
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 6, alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
          <input type="radio" checked={type === ''}         onChange={() => changeType('')} /> None
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
          <input type="radio" checked={type === 'contact'}  onChange={() => changeType('contact')} /> Contact
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
          <input type="radio" checked={type === 'business'} onChange={() => changeType('business')} /> Member
        </label>
      </div>

      {type && (
        selected ? (
          <div style={{
            padding: '8px 12px', background: '#eef6ff', borderRadius: 6,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
              {type === 'contact' ? '👤' : '🏢'} {selected.name}
            </span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={clear}>Change</button>
          </div>
        ) : (
          <>
            <input
              className="form-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={type === 'contact' ? 'Search contacts…' : 'Search members…'}
            />
            {searching && <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 4 }}>Searching…</div>}
            {results.length > 0 && (
              <div style={{ marginTop: 4, border: '1px solid var(--gray-200)', borderRadius: 6, maxHeight: 180, overflowY: 'auto' }}>
                {results.map(r => (
                  <div key={r.id} onClick={() => pick(r)}
                       style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)' }}
                       onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                       onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{r.name}</div>
                    {r.subtitle && <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{r.subtitle}</div>}
                  </div>
                ))}
              </div>
            )}
          </>
        )
      )}
    </div>
  );
}

function TaskDetailPanel({ taskId, staffList, onClose, onUpdated }) {
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [linkedEntity, setLinkedEntity] = useState({ type: null, id: null, name: null });

  useEffect(() => {
    api.get(`/tasks/${taskId}`)
      .then(res => {
        setTask(res.data);
        setEditForm({
          title: res.data.title,
          description: res.data.description,
          priority: res.data.priority,
          status: res.data.status,
          dueDate: res.data.due_date?.split('T')[0] || '',
          assignedToStaffId: res.data.assigned_to_staff_id || '',
        });
        setLinkedEntity({
          type: res.data.linked_entity_type || null,
          id:   res.data.linked_entity_id   || null,
          name: res.data.linked_entity_name || null,
        });
      })
      .catch(() => toast.error('Failed to load task.'))
      .finally(() => setLoading(false));
  }, [taskId]);

  const handleUpdate = async () => {
    try {
      await api.patch(`/tasks/${taskId}`, {
        ...editForm,
        linkedEntityType: linkedEntity.type,
        linkedEntityId:   linkedEntity.id,
      });
      toast.success('Task updated.');
      setEditing(false);
      onUpdated();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/tasks/${taskId}/notes`, { noteText });
      setNoteText('');
      const res = await api.get(`/tasks/${taskId}`);
      setTask(res.data);
    } catch (err) { toast.error('Failed to add note.'); }
  };

  if (loading) return <div className="card" style={{ padding: 20 }}><div className="spinner" /></div>;
  if (!task) return null;

  const isSnoozed = task.snoozed_until && new Date(task.snoozed_until) > new Date();

  return (
    <div className="card" style={{ height: 'fit-content', position: 'sticky', top: 80 }}>
      <div className="card-header">
        <span className="card-title" style={{ fontSize: '0.875rem' }}>Task Detail</span>
        <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
      </div>
      <div style={{ padding: 16, maxHeight: '75vh', overflowY: 'auto' }}>
        {!editing ? (
          <>
            <div style={{ marginBottom: 12 }}>
              <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>{task.title}</h3>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                <span className={`badge ${STATUS_COLORS[task.status]}`}>{task.status?.replace('_',' ')}</span>
                <span style={{ fontSize: '0.72rem', background: PRIORITY_COLORS[task.priority]?.bg, color: PRIORITY_COLORS[task.priority]?.color, padding: '2px 8px', borderRadius: 12, fontWeight: 700 }}>
                  {PRIORITY_COLORS[task.priority]?.label}
                </span>
                {isSnoozed && (
                  <span style={{ fontSize: '0.72rem', background: '#ede9fe', color: '#7c3aed', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                    💤 until {fmtDateTime(task.snoozed_until)}
                  </span>
                )}
              </div>
              {task.description && <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', whiteSpace: 'pre-wrap' }}>{task.description}</p>}
            </div>

            <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginBottom: 12 }}>
              {task.assigned_to_first_name && <div>Assigned to: <strong>{task.assigned_to_first_name} {task.assigned_to_last_name}</strong></div>}
              {task.due_date && <div style={{ color: new Date(task.due_date) < new Date() && task.status !== 'complete' ? 'var(--danger)' : undefined }}>Due: <strong>{fmtDate(task.due_date)}</strong></div>}
              <div>Created by: {task.created_by_first_name} {task.created_by_last_name} · {fmtDate(task.created_at)}</div>
              {task.linked_entity_name && (
                <div style={{ marginTop: 4 }}>
                  Linked to: <LinkedEntityChip
                    type={task.linked_entity_type}
                    id={task.linked_entity_id}
                    name={task.linked_entity_name}
                  />
                </div>
              )}
            </div>

            <button className="btn btn-secondary btn-sm" style={{ marginBottom: 16 }} onClick={() => setEditing(true)}>Edit Task</button>
          </>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Title</label>
              <input className="form-input" value={editForm.title} onChange={e => setEditForm(f => ({...f, title: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-textarea" rows={3} value={editForm.description || ''} onChange={e => setEditForm(f => ({...f, description: e.target.value}))} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={editForm.status} onChange={e => setEditForm(f => ({...f, status: e.target.value}))}>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="pending">Pending</option>
                  <option value="complete">Complete</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select className="form-select" value={editForm.priority} onChange={e => setEditForm(f => ({...f, priority: e.target.value}))}>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-input" type="date" value={editForm.dueDate} onChange={e => setEditForm(f => ({...f, dueDate: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Assigned To</label>
              <select className="form-select" value={editForm.assignedToStaffId} onChange={e => setEditForm(f => ({...f, assignedToStaffId: e.target.value}))}>
                <option value="">Unassigned</option>
                {staffList.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Link To (optional)</label>
              <LinkedEntityPicker value={linkedEntity} onChange={setLinkedEntity} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleUpdate}>Save</button>
            </div>
          </div>
        )}

        {/* Notes */}
        <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 12 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 10 }}>
            Notes ({task.notes?.length || 0})
          </div>
          {task.notes?.map(note => (
            <div key={note.id} style={{ marginBottom: 10, padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 'var(--radius)', fontSize: '0.8rem' }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{note.first_name} {note.last_name} <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>· {fmtDateTime(note.created_at)}</span></div>
              <div style={{ color: 'var(--gray-700)', whiteSpace: 'pre-wrap' }}>{note.note_text}</div>
            </div>
          ))}
          <textarea
            className="form-textarea"
            rows={2}
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder="Add a note..."
            style={{ fontSize: '0.8rem', marginTop: 6 }}
          />
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} onClick={handleAddNote} disabled={!noteText.trim()}>
            Add Note
          </button>
        </div>
      </div>
    </div>
  );
}

// [marker: invoice-links v1] exported so a task can be added right from a contact or company page
export function CreateTaskModal({ staffList: staffProp, initialLink, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', description: '', priority: 'normal', dueDate: '', assignedToStaffId: '' });
  const [linkedEntity, setLinkedEntity] = useState(initialLink || { type: null, id: null, name: null });
  const [staffLoaded, setStaffLoaded] = useState([]);
  useEffect(() => {
    if (!staffProp) api.get('/staff/users').then(r => setStaffLoaded(r.data || [])).catch(() => {});
  }, [staffProp]);
  const staffList = staffProp || staffLoaded;
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Title is required.');
    setSaving(true);
    try {
      await api.post('/tasks', {
        ...form,
        linkedEntityType: linkedEntity.type,
        linkedEntityId:   linkedEntity.id,
      });
      toast.success('Task created.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">New Task</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="What needs to be done?" autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Add details, checklist items, or instructions..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-select" value={form.priority} onChange={e => set('priority', e.target.value)}>
                <option value="urgent">🔴 Urgent</option>
                <option value="high">🟡 High</option>
                <option value="normal">🔵 Normal</option>
                <option value="low">⚪ Low</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-input" type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Assign To</label>
            <select className="form-select" value={form.assignedToStaffId} onChange={e => set('assignedToStaffId', e.target.value)}>
              <option value="">Myself</option>
              {staffList.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} — {s.role}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Link To (optional)</label>
            <LinkedEntityPicker value={linkedEntity} onChange={setLinkedEntity} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Creating...' : 'Create Task'}</button>
        </div>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
